import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useFormik } from 'formik';
import { Drawer, drawerType } from '../../../../packages/ui/organisms/drawer';
import { Input, inputSize } from '../../../../packages/ui/atoms/input';
import { Text, textLevel, fontWeight, colors, TextError } from '../../../../packages';
import DateSelect from '../../../../packages/ui/templates/date-select';
import { DiscontinueRXSchema } from './DiscontinueRXDrawer.schema';

const DiscontinueRXDrawer = ({ open, onClose, onContinue, medication }) => {
  const form = useFormik({
    initialValues: {
      reason: '',
      time: new Date(),
    },
    validationSchema: DiscontinueRXSchema,
    onSubmit: values => {
      if (onContinue) {
        onContinue(values.reason, values.time, medication);
      }
      form.resetForm({
        values: {
          reason: '',
          time: new Date(),
        },
      });
      onClose();
    },
  });

  const { values, errors, touched, handleChange, setFieldValue, setTouched, resetForm } = form;

  useEffect(() => {
    if (open) {
      resetForm({
        values: {
          reason: '',
          time: new Date(),
        },
      });
    }
  }, [open, resetForm]);

  const handleClose = () => {
    resetForm({
      values: {
        reason: '',
        time: new Date(),
      },
    });
    onClose();
  };

  const handleContinue = async () => {
    await setTouched({
      reason: true,
      time: true,
    });
    form.handleSubmit();
  };

  const handleDateChange = date => {
    setFieldValue('time', date);
  };

  const handleReasonBlur = () => {
    setTouched({ ...touched, reason: true });
  };

  const isDateValid = () => {
    if (!values.time) return false;
    const selectedDate = new Date(values.time);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate >= today || selectedDate < today;
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      variant={drawerType.FORM}
      title="Discontinue Medication"
      submitBtnTitle="Continue"
      onSubmit={handleContinue}
      disableSubmit={!values.reason.trim() || !values.time || !isDateValid()}
    >
      <Box display="flex" flexDirection="column" gap="24px">
        <Box display="flex" flexDirection="column" gap="8px">
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Text level={textLevel.M} weight={fontWeight.BOLD} color={colors.neutral900}>
              Reason for discontinuation
            </Text>
            {touched.reason && errors.reason && <TextError errorMsg={errors.reason} />}
          </Box>
          <Input
            fullWidth
            size={inputSize.S}
            placeholder="Record your reason for discontinuing the medication here"
            name="reason"
            value={values.reason}
            onChange={handleChange}
            onBlur={handleReasonBlur}
            multiline
          />
        </Box>
        <Box display="flex" flexDirection="column" gap="8px">
          <Text level={textLevel.M} weight={fontWeight.BOLD} color={colors.neutral900}>
            Date of discontinuation
          </Text>
          <DateSelect value={values.time} onChange={handleDateChange} noValidate />
        </Box>
      </Box>
    </Drawer>
  );
};

export default DiscontinueRXDrawer;
